import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
    StyleSheet,
    View,
    FlatList,
    ActivityIndicator,
    useWindowDimensions,
    TouchableOpacity,
    Text,
    ScrollView
} from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { useCompany } from "../../../hooks/useCompany";
import { useAccountContext } from "../../../hooks/useAccountContext";
import { showMessage } from "react-native-flash-message";
import { v4 as uuidv4 } from 'uuid';
import { Image } from "react-native";
import { DEFAULTPROFILE } from "../../../img/DefaultProfile";
import CustomInput from "../../../components/CustomInput";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import { createCommonStyles } from "../../../styles/commonStyles";




const UserListItem = React.memo(({ item, onAdd, onRemove, isAuthorized, styles, iconSize = 28,
    iconColor: propIconColor,
    removeIconColor: propRemoveColor, highlight, }) => {

    const { getEmployeePhoto } = useCompany();
    const [photoUri, setPhotoUri] = useState(null);
    const highlightColor = highlight === 'added' ? '#d4edda' : highlight === 'removed' ? '#f8d7da' : 'transparent';
    const iconColor = propIconColor ?? "#5C8F1A";
    const removeIconColor = propRemoveColor ?? "#B91C1C";
    const displayName = item.name ?? item.EmployeeName ?? item.employeeid ?? "Unknown User";

    useEffect(() => {
        getEmployeePhoto(item.employeeid).then(setPhotoUri);
    }, [item.employeeid]);

    return (
        <View style={[styles.rowItem, { backgroundColor: highlightColor }]}>
            <View style={{ flexDirection: 'row', flex: 1, alignItems: 'center' }}>
                <Image
                    source={{ uri: photoUri ?? `data:image/png;base64,${DEFAULTPROFILE}` }}
                    style={styles.employeePhotograph}
                />
                <View style={{ marginLeft: 10 }}>
                    <Text style={styles.nameText}>{displayName}</Text>
                </View>
            </View>
            <View style={{ width: 100, flexDirection: 'row', justifyContent: 'flex-end' }}>
                {isAuthorized ? (
                    <TouchableOpacity
                        onPress={() => onRemove(item)}
                        style={{ padding: 8 }}
                        accessibilityLabel="Remove user"
                    >
                        <Ionicons name="remove-circle-outline" size={iconSize} color={removeIconColor} />
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity
                        onPress={() => onAdd(item)}
                        style={{ padding: 8 }}
                        accessibilityLabel="Add user"
                    >
                        <Ionicons name="add-circle-outline" size={iconSize} color={iconColor} />
                    </TouchableOpacity>
                )}
            </View>
        </View>
    );
});

const HRAdminAuthorizationsScreen = (props) => {
    const { selectedCompany } = props;
    const { t } = useTranslation();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);

    const { width, height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(colors, width, height), [colors, width, height]);

    const isWideScreen = width > 1300;
    const listMaxHeight = isWideScreen ? height * 0.6 : height * 0.45;

    const { getAuthorizedUsers, getRemainingUsers, saveAuthorizedUsers } = useCompany();
    const { account } = useAccountContext();

    const [authorized, setAuthorized] = useState([]);
    const [remaining, setRemaining] = useState([]);
    const [originalAuthorized, setOriginalAuthorized] = useState([]);
    const [originalRemaining, setOriginalRemaining] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const mountedRef = useRef(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermRemaining, setSearchTermRemaining] = useState('');

    const authorizedListRef = useRef(null);
    const remainingListRef = useRef(null);
    const [recentlyChanged, setRecentlyChanged] = useState({});


    useEffect(() => {
        mountedRef.current = true;
        loadData();

        return () => {
            mountedRef.current = false;
        };
    }, [selectedCompany]);

    const loadData = async () => {
        if (!selectedCompany) {
            setAuthorized([]);
            setRemaining([]);
            setOriginalAuthorized([]);
            setOriginalRemaining([]);
            setHasChanges(false);
            setRecentlyChanged({})
            return;
        }
        setIsLoading(true);
        try {
            const [authResp, remResp] = await Promise.all([
                getAuthorizedUsers(selectedCompany),
                getRemainingUsers(selectedCompany),
            ]);
            // normalize to objects with employeeid & name
            const normAuth = Array.isArray(authResp)
                ? authResp.map(r => {
                    const name = r.name ?? r.EmployeeName ?? r.employeeName ?? r.EmployeeFullName ?? "";
                    return {
                        employeeid: r.employeeid ?? r.EmployeeID ?? r.employeeID ?? r.employeeId,
                        name,
                        rowGuid: r.rowGuid ?? uuidv4(),
                    };
                })
                : [];

            const normRem = Array.isArray(remResp)
                ? remResp.map(r => {
                    const name = r.name ?? r.EmployeeName ?? r.employeeName ?? r.EmployeeFullName ?? "";
                    return {
                        employeeid: r.employeeid ?? r.EmployeeID ?? r.employeeID ?? r.employeeId,
                        name,
                        rowGuid: r.rowGuid ?? uuidv4(),
                    };
                })
                : [];
            if (!mountedRef.current) return;
            setAuthorized(normAuth);
            setRemaining(normRem);
            setOriginalAuthorized(normAuth);
            setOriginalRemaining(normRem);
            setHasChanges(false);
        } catch (err) {
            console.error("Error loading authorised/remaining users:", err);
            showMessage({
                message: t("common:ErrorLoadingAuthorizations") || "Failed to load authorizations",
                type: "danger",
                duration: 3000,
            });
            setAuthorized([]);
            setRemaining([]);
            setOriginalAuthorized([]);
            setOriginalRemaining([]);
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    };

    const handleAdd = useCallback((user) => {
        if (!user?.employeeid) return;
        setAuthorized(prev => {
            if (prev.some(a => a.employeeid === user.employeeid)) return prev;
            return [user, ...prev];
        });
        setRemaining(prev => prev.filter(r => r.employeeid !== user.employeeid));
        setHasChanges(true);
        setRecentlyChanged(prev => ({ ...prev, [user.employeeid]: 'added' }));
    }, []);

    const handleRemove = useCallback((employee) => {
        if (!employee?.employeeid) return;
        if (employee.employeeid === account?.employeeId) return;
        setRemaining(prev => [employee, ...prev]);
        setAuthorized(prev => prev.filter(a => a.employeeid !== employee.employeeid));
        setHasChanges(true);
        setRecentlyChanged(prev => ({ ...prev, [employee.employeeid]: 'removed' }));
    }, [account]);

    // add all remaining to authorized
    const handleAddAll = () => {
        const toAdd = remaining.filter(r => !authorized.some(a => a.employeeid === r.employeeid));
        setAuthorized(prev => [...toAdd, ...prev]);
        setRemaining([]);
        setHasChanges(true);
        authorizedListRef.current?.scrollToOffset({ offset: 0, animated: true });
        setRecentlyChanged(prev => ({
            ...prev,
            ...Object.fromEntries(toAdd.map(u => [u.employeeid, 'added']))
        }));
    };

    // remove all remaining to authorized
    const handleRemoveAll = () => {
        const toRemove = authorized.filter(a =>
            !remaining.some(r => r.employeeid === a.employeeid) &&
            a.employeeid !== account?.employeeId
        );
        setRemaining(prev => [...toRemove, ...prev]);
        setAuthorized(prev => prev.filter(a => a.employeeid === account?.employeeId));
        setHasChanges(true);
        remainingListRef.current?.scrollToOffset({ offset: 0, animated: true });
        setRecentlyChanged(prev => ({
            ...prev,
            ...Object.fromEntries(toRemove.map(u => [u.employeeid, 'removed']))
        }));
    };

    const handleSave = async () => {
        if (!selectedCompany) {
            showMessage({ message: t("common:noCompanySelected") || "No company selected", type: "warning" });
            return;
        }
        setIsLoading(true);
        try {
            // Compute employee IDs for differences
            const originalAuthIds = new Set(originalAuthorized.map(u => u.employeeid));
            const currentAuthIds = new Set(authorized.map(u => u.employeeid));

            const usersToAdd = [...currentAuthIds].filter(id => !originalAuthIds.has(id));
            const usersToRemove = [...originalAuthIds].filter(id => !currentAuthIds.has(id));

            const payload = {
                company: selectedCompany,
                usersToAdd,
                usersToRemove,
            };

            const result = await saveAuthorizedUsers(payload);
            if (result) {
                showMessage({
                    message: t("common:authSuccessSave"),
                    type: "success",
                    duration: 3000,
                });
                setOriginalAuthorized(authorized);
                setOriginalRemaining(remaining);
                setHasChanges(false);
                setRecentlyChanged({});
            } else {
                showMessage({
                    message: t("common:failedSave"),
                    type: "danger",
                    duration: 3000,
                });
            }
        } catch (err) {
            console.error("Error saving authorizations:", err);
            showMessage({
                message: t("common:failedSave"),
                type: "danger",
                duration: 3000,
            });
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    };

    const handleCancel = () => {
        // revert to original state
        setAuthorized(originalAuthorized || []);
        setRemaining(originalRemaining || []);
        setHasChanges(false);
        setRecentlyChanged({});
        showMessage({
            message: t("common:changesCancelled") || "Changes cancelled",
            type: "info",
            duration: 2000,
        });
    };

    // Render helpers
    const renderAuthorizedItem = useCallback(({ item }) => (
        <UserListItem
            item={item}
            onRemove={handleRemove}
            isAuthorized={true}
            styles={styles}
            highlight={recentlyChanged[item.employeeid]}
            currentUserId={account?.employeeId}
        />
    ), [handleRemove, styles, recentlyChanged, account]);

    const renderRemainingItem = useCallback(({ item }) => (
        <UserListItem
            item={item}
            onAdd={handleAdd}
            isAuthorized={false}
            styles={styles}
            highlight={recentlyChanged[item.employeeid]}
            currentUserId={account?.employeeId}
        />
    ), [handleAdd, styles, recentlyChanged, account]);


    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <ScrollView style={{ flex: 1 }}>
            <View style={styles.mainView}>
                <View style={styles.headerButtons}>
                    <CustomButton
                        text={t("common:addAll") || "Add All"}
                        onPress={handleAddAll}
                        type="BUTTON_ADD"
                    />
                    <CustomButton
                        text={t("common:removeAll") || "Remove All"}
                        onPress={handleRemoveAll}
                        type="BUTTON_CANCEL"
                    />
                    <CustomButton
                        text={t("common:save") || "Save"}
                        onPress={handleSave}
                        type="BUTTON_SAVE"
                        isGradient={true}
                        disabled={!hasChanges}
                    />
                    <CustomButton
                        text={t("common:cancel") || "Cancel"}
                        onPress={handleCancel}
                        type="BUTTON_CANCEL"
                        disabled={!hasChanges}
                    />
                </View>
                <View style={styles.listsContainer}>
                    <Card style={[common.cardstyle, styles.column]}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                            <CustomLabel
                                type="SUBTITLEADMIN"
                                value={t("common:availableUsers")}
                            >
                            </CustomLabel>
                        </View>
                        <View style={styles.search}>
                            <CustomInput
                                editable={true}
                                style={styles.searchInput}
                                placeholder={`${t('common:search')}...`}
                                value={searchTermRemaining}
                                setValue={setSearchTermRemaining}
                                clearButtonMode="while-editing"
                                type='SELECT_EMPLOYEE'
                            />
                        </View>
                        <Card.Content>
                            <View style={{ maxHeight: listMaxHeight }}>
                                <FlatList
                                    ref={remainingListRef}
                                    data={remaining.filter(user =>
                                        (user.name ?? "").toLowerCase().includes(searchTermRemaining.toLowerCase())
                                    )}
                                    keyExtractor={(item) => String(item.employeeid)}
                                    renderItem={renderRemainingItem}
                                    ListEmptyComponent={<Text style={styles.muted}>{t("common:noAvailableUsers")}</Text>}
                                />
                            </View>
                        </Card.Content>
                    </Card>

                    <Card style={[common.cardstyle, styles.column]}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                            <CustomLabel
                                type="SUBTITLEADMIN"
                                value={t("common:authorisedUsers")}
                            >
                            </CustomLabel>
                        </View>
                        <View style={styles.search}>
                            <CustomInput
                                editable={true}
                                style={styles.searchInput}
                                placeholder={`${t('common:search')}...`}
                                value={searchTerm}
                                setValue={setSearchTerm}
                                clearButtonMode="while-editing"
                                type='SELECT_EMPLOYEE'
                            />
                        </View>
                        <Card.Content>
                            <View style={{ maxHeight: listMaxHeight }}>
                                <FlatList

                                    ref={authorizedListRef}
                                    data={authorized.filter(user =>
                                        (user.name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
                                    )}
                                    keyExtractor={(item) => String(item.employeeid)}
                                    renderItem={renderAuthorizedItem}
                                    ListEmptyComponent={<Text style={styles.muted}>{t("common:noAuthorisedUsers")}</Text>}
                                />
                            </View>
                        </Card.Content>
                    </Card>
                </View>
            </View >
        </ScrollView>
    );
};

const makeStyles = (colors, width, height) =>
    StyleSheet.create({
        mainView: {
            flex: 1,
            backgroundColor: colors.background || "#fff",
        },
        headerButtons: {
            flexDirection: "row",
            justifyContent: "Flex-end",
            alignItems: "center",
            marginHorizontal: 15,
        },
        title: {
            fontSize: 20,
            fontWeight: "bold",
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
        },
        listsContainer: {
            flex: 1,
            flexDirection: width > 700 ? "row" : "column",
            justifyContent: "space-between",
            gap: 12,
            marginHorizontal: 5,
            marginVertical: 8,
        },
        column: {
            flex: 2,
            marginHorizontal: 5,
        },
        rowItem: {
            flexDirection: "row",
            alignItems: "center",
            paddingVertical: 5,
            paddingHorizontal: 12,
            marginVertical: 2,
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: "#e0e0e0",
        },
        nameText: {
            fontSize: 14,
            fontWeight: "500",
        },
        muted: {
            fontSize: 12,
            color: "#666",
            marginTop: 4,
        },
        employeePhotograph: {
            width: 30,
            height: 30,
            borderRadius: 15,
            borderWidth: 1,
            borderColor: '#ccc',
        },
        searchInput: {
            backgroundColor: colors.background,
        },
        search: {
            marginHorizontal: 2,
        }
    });

export default HRAdminAuthorizationsScreen;